import React, { useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import {
    FaUser,
    FaEnvelope,
    FaPhone,
    FaComment,
    FaPaperPlane,
} from "react-icons/fa";
import Header from "./Header.js";
import VertNav from "./VertNav.js";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../../config.js";

// Enhanced Input Component
const InputField = ({
    icon: Icon,
    type,
    name,
    placeholder,
    value,
    onChange,
    error,
    isTextarea = false,
}) => {
    const inputClasses = `pl-10 w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition ${
        error ? "border-red-500" : "border-gray-300"
    }`;

    return (
        <div className="space-y-1">
            <div className="relative">
                <div
                    className={`absolute inset-y-0 left-0 pl-3 ${
                        isTextarea
                            ? "flex items-start pt-3"
                            : "flex items-center"
                    } pointer-events-none`}
                >
                    <Icon className="text-gray-400" />
                </div>
                {isTextarea ? (
                    <textarea
                        name={name}
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                        rows="5"
                        className={inputClasses}
                    />
                ) : (
                    <input
                        type={type}
                        name={name}
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                        className={inputClasses}
                    />
                )}
            </div>
            {error && (
                <p className="mt-1 text-sm text-red-600 animate-fadeIn">
                    {error}
                </p>
            )}
        </div>
    );
};

// Enhanced Form Section Component
const FormSection = ({ children, title, description }) => (
    <div className="w-full max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-6">
        <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">{title}</h2>
            {description && <p className="text-gray-600">{description}</p>}
        </div>
        {children}
    </div>
);

// Main Help Component
function Help() {
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        message: "",
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: null,
            });
        }
    };

    const validate = () => {
        let errors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^\d{10}$/;

        if (!formData.firstName.trim())
            errors.firstName = "First name is required";
        if (!formData.lastName.trim())
            errors.lastName = "Last name is required";

        if (!formData.email.trim()) {
            errors.email = "Email is required";
        } else if (!emailRegex.test(formData.email)) {
            errors.email = "Please enter a valid email address";
        }

        if (!formData.phoneNumber.trim()) {
            errors.phoneNumber = "Phone number is required";
        } else if (!phoneRegex.test(formData.phoneNumber)) {
            errors.phoneNumber = "Phone number must be 10 digits";
        }

        if (!formData.message.trim()) errors.message = "Message is required";

        setErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setIsSubmitting(true);
        const data = {
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email,
            mobile_no: formData.phoneNumber,
            message: formData.message,
        };

        try {
            const token = localStorage.getItem("token");

            const response = await fetch(
                `${config.apiUrl}/api/swalook/help_desk/`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Token ${token}`,
                    },
                    body: JSON.stringify(data),
                }
            );

            if (response.status === 200 || response.status === 201) {
                toast.success("Your message has been sent successfully!", {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    className: "bg-green-50 text-green-800",
                });
                setFormData({
                    firstName: "",
                    lastName: "",
                    email: "",
                    phoneNumber: "",
                    message: "",
                });
                setErrors({});
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("Failed to send message. Please try again.", {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                className: "bg-red-50 text-red-800",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Helmet>
                <title>Help Desk</title>
            </Helmet>
            <Header />
            <VertNav />
            <ToastContainer />

            <div className="md:ml-64 p-4">
                <div className="flex justify-center items-start p-4 md:p-8">
                    <FormSection
                        title="Help Desk"
                        description="We're here to help you with any questions or issues"
                    >
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        First Name *
                                    </label>
                                    <InputField
                                        icon={FaUser}
                                        type="text"
                                        name="firstName"
                                        placeholder="Enter First Name"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        error={errors.firstName}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Last Name *
                                    </label>
                                    <InputField
                                        icon={FaUser}
                                        type="text"
                                        name="lastName"
                                        placeholder="Enter Last Name"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        error={errors.lastName}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email *
                                </label>
                                <InputField
                                    icon={FaEnvelope}
                                    type="email"
                                    name="email"
                                    placeholder="Enter e-mail"
                                    value={formData.email}
                                    onChange={handleChange}
                                    error={errors.email}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Phone Number *
                                </label>
                                <InputField
                                    icon={FaPhone}
                                    type="tel"
                                    name="phoneNumber"
                                    placeholder="Enter mobile number (10-digits)"
                                    value={formData.phoneNumber}
                                    onChange={handleChange}
                                    error={errors.phoneNumber}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    How can we help you? *
                                </label>
                                <InputField
                                    icon={FaComment}
                                    name="message"
                                    placeholder="Describe your issue or question..."
                                    value={formData.message}
                                    onChange={handleChange}
                                    error={errors.message}
                                    isTextarea={true}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full flex items-center justify-center py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition duration-200 shadow-md hover:shadow-lg ${
                                    isSubmitting
                                        ? "opacity-70 cursor-not-allowed"
                                        : ""
                                }`}
                            >
                                <FaPaperPlane className="mr-2" />
                                {isSubmitting ? "Sending..." : "SEND MESSAGE"}
                            </button>
                        </form>
                    </FormSection>
                </div>
            </div>
        </div>
    );
}

export default Help;
